import axios from "axios";
import i18next from "i18next";
import { setupCache } from "axios-cache-adapter";

import store from "../../redux-saga/store";
import {
  navigateTarget,
  showErrorMessage
} from "../../redux-saga/redux/toast/index";
import { resetAuthenticationState } from "../../redux-saga/redux/auth/authentication";
import { resetMyProfileState } from "../../redux-saga/redux/profile/myProfile";
import { resetTransactionState } from "../../redux-saga/redux/profile/transactions";
import { getLanguageCode } from "../../container/SportsBookNode/utils/common/constants";
import { updateAppStatus } from "../../network";
import { getTranslation } from "../../utils/helper";
import Session from "../../utils/session";
import { resetPaymentState } from "../../redux-saga/redux/payment";

const tokens = Session.getSessionCred;
const cache = setupCache({
  maxAge: 60 * 1000 * 60,
  exclude: {
    query: false
  },
  clearOnStale: true,
  clearOnError: true
});
const axiosClient = axios.create({
  baseURL: process.env.REACT_APP_SPORTSBOOK_NODE_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    "domain-key": process.env.REACT_APP_SPORTSBOOK_NODE_DOMAIN_KEY
  },
  maxConcurrentRequests: 10
});
const axiosClientCached = axios.create({
  baseURL: process.env.REACT_APP_SPORTSBOOK_NODE_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    "domain-key": process.env.REACT_APP_SPORTSBOOK_NODE_DOMAIN_KEY
  },
  maxConcurrentRequests: 10,
  adapter: cache.adapter
});
if (tokens?.token && tokens?.resToken) {
  axiosClient.defaults.headers.common["access-token"] = tokens?.token;
  axiosClient.defaults.headers.common["refresh-token"] = tokens?.resToken;
}

axiosClient.interceptors.response.use(
  (res) => res,
  (err) => {
    if (!window.navigator.onLine) {
      throw new axios.Cancel(getTranslation("LOBBY.CONNECT_INTERNET"));
    }

    if (err?.response) {
      const originalRequest = err.config;

      if (
        err.response?.status === 401 &&
        originalRequest?.url === "/user/refreshToken" &&
        originalRequest?.url !== "/user/login"
      ) {
        store.dispatch(showErrorMessage({ tKey: "HEADER.SESSION_TIMEOUT" }));
        Session.clearSessionCred();
        store.dispatch(navigateTarget("/"));
        store.dispatch(resetMyProfileState());
        store.dispatch(resetTransactionState());
        store.dispatch(resetAuthenticationState());
        store.dispatch(resetPaymentState());
        return { data: "" };
      }

      if (
        err.response.status === 401 &&
        !originalRequest._retry &&
        originalRequest?.url !== "/user/login"
      ) {
        originalRequest._retry = true;
        return axiosClient
          .get("/user/refreshToken")
          .then(({ data }) => {
            if (data) {
              const { token, resToken } = data;
              const { userId } = Session.getSessionCred;
              Session.saveSessionCred({
                token,
                resToken,
                userId
              });
              // Change Authorization header
              axiosClient.defaults.headers.common["access-token"] = token;
              axiosClient.defaults.headers.common["refresh-token"] = resToken;
              // return originalRequest object with Axios.
              return axiosClient(originalRequest);
            } else {
              return Promise.reject(err?.response);
            }
          })
          .catch((refreshError) => {
            return Promise.reject(refreshError);
          });
      }

      if (err?.response?.data) {
        const {
          casinoShutdown = null,
          sportsbookShutdown = null,
          totalShutdown = null
        } = err.response.data;
        updateAppStatus({
          casinoShutdown,
          sportsbookShutdown,
          totalShutdown
        });
      }

      return Promise.reject(err.response);
    }
    return Promise.reject(err);
  }
);

axiosClient.interceptors.request.use((req) => {
  if (!window.navigator.onLine) {
    throw new axios.Cancel(getTranslation("LOBBY.CONNECT_INTERNET"));
  }
  const tokens = Session.getSessionCred;
  if (tokens) {
    req.headers["access-token"] = tokens.token;
    req.headers["refresh-token"] = tokens.resToken;
  }
  req.headers["language"] = getLanguageCode(i18next?.language?.toLowerCase());

  return req;
});

axiosClientCached.interceptors.response.use(
  (res) => res,
  (err) => {
    if (!window.navigator.onLine) {
      throw new axios.Cancel(getTranslation("LOBBY.CONNECT_INTERNET"));
    }

    if (err?.response) {
      const originalRequest = err.config;

      if (
        err.response?.status === 401 &&
        originalRequest?.url === "/user/refreshToken" &&
        originalRequest?.url !== "/user/login"
      ) {
        store.dispatch(showErrorMessage({ tKey: "HEADER.SESSION_TIMEOUT" }));
        Session.clearSessionCred();
        store.dispatch(navigateTarget("/"));
        store.dispatch(resetMyProfileState());
        store.dispatch(resetTransactionState());
        store.dispatch(resetAuthenticationState());
        store.dispatch(resetPaymentState());
        return { data: "" };
      }

      if (
        err.response.status === 401 &&
        !originalRequest._retry &&
        originalRequest?.url !== "/user/login"
      ) {
        originalRequest._retry = true;
        return axiosClientCached
          .get("/user/refreshToken")
          .then(({ data }) => {
            if (data) {
              const { token, resToken } = data;
              const { userId } = Session.getSessionCred;
              Session.saveSessionCred({
                token,
                resToken,
                userId
              });
              // Change Authorization header
              axiosClientCached.defaults.headers.common["access-token"] = token;
              axiosClientCached.defaults.headers.common["refresh-token"] =
                resToken;
              // return originalRequest object with Axios.
              return axiosClientCached(originalRequest);
            } else {
              return Promise.reject(err?.response);
            }
          })
          .catch((refreshError) => {
            return Promise.reject(refreshError);
          });
      }

      if (err?.response?.data) {
        const {
          casinoShutdown = null,
          sportsbookShutdown = null,
          totalShutdown = null
        } = err.response.data;
        updateAppStatus({
          casinoShutdown,
          sportsbookShutdown,
          totalShutdown
        });
      }

      return Promise.reject(err.response);
    }
    return Promise.reject(err);
  }
);

axiosClientCached.interceptors.request.use((req) => {
  if (!window.navigator.onLine) {
    throw new axios.Cancel(getTranslation("LOBBY.CONNECT_INTERNET"));
  }
  const tokens = Session.getSessionCred;
  if (tokens) {
    req.headers["access-token"] = tokens.token;
    req.headers["refresh-token"] = tokens.resToken;
  }
  req.headers["language"] = getLanguageCode(i18next?.language?.toLowerCase());

  return req;
});

export { axiosClientCached };
export default axiosClient;
